import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { athletesQuery, enrollmentsQuery, formatPrice, programsQuery, type Athlete, type Program } from "@/lib/family";
import { gradeLabel, gradeRangeLabel, isGradeInRange } from "@/lib/grades";

export const Route = createFileRoute("/_app/home")({
  component: FamilyHome,
});

function FamilyHome() {
  const { profile } = Route.useRouteContext();
  const athletes = useQuery(athletesQuery);
  const programs = useQuery(programsQuery);
  const enrollments = useQuery(enrollmentsQuery);

  return (
    <div className="space-y-10">
      <section>
        <h1 className="font-display text-4xl font-extrabold uppercase italic">
          Welcome{profile.first_name ? `, ${profile.first_name}` : ""}.
        </h1>
        <p className="mt-2 text-white/70">
          Manage your players and Winter Season registration.{" "}
          <Link to="/waiver" className="underline decoration-white/30 underline-offset-4 hover:text-white">
            Registration agreement
          </Link>
        </p>
      </section>

      <section>
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-display text-2xl font-extrabold uppercase italic">Your players</h2>
          <Link to="/players/new" className="rounded-md border border-white/20 px-3 py-1.5 text-sm font-semibold hover:border-white/40">
            + Add player
          </Link>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {athletes.isLoading && <p className="text-sm text-white/60">Loading…</p>}
          {athletes.data?.length === 0 && (
            <p className="rounded-md border border-dashed border-white/15 p-5 text-sm text-white/60 sm:col-span-2">
              No players yet. Add your daughter to register her for the season.
            </p>
          )}
          {athletes.data?.map((a) => <PlayerCard key={a.id} athlete={a} />)}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl font-extrabold uppercase italic">Winter Season</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {programs.data?.map((p) => (
            <ProgramCard
              key={p.id}
              program={p}
              athletes={athletes.data ?? []}
              enrolledIds={new Set((enrollments.data ?? []).filter((e) => e.program_id === p.id).map((e) => e.athlete_id))}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function PlayerCard({ athlete }: { athlete: Athlete }) {
  const name = athlete.preferred_name || athlete.first_name;
  return (
    <Link
      to="/players/$athleteId"
      params={{ athleteId: athlete.id }}
      className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] p-4 hover:border-white/25"
    >
      <div>
        <p className="font-semibold">
          {name} {athlete.last_name}
        </p>
        <p className="text-sm text-white/60">{gradeLabel(athlete.grade)}</p>
      </div>
      <span className="text-xs text-white/50">Edit</span>
    </Link>
  );
}

function ProgramCard({ program, athletes, enrolledIds }: { program: Program; athletes: Athlete[]; enrolledIds: Set<string> }) {
  const eligible = athletes.filter((a) => isGradeInRange(a.grade, program.grade_min, program.grade_max));
  return (
    <article className="rounded-md border border-white/10 bg-white/[0.03] p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-a1-red">{program.season}</p>
      <h3 className="mt-1 text-lg font-semibold">{program.name}</h3>
      {program.short_description && <p className="mt-1 text-sm text-white/70">{program.short_description}</p>}
      <p className="mt-3 font-display text-3xl font-extrabold italic">{formatPrice(program.price_cents)}</p>
      <p className="text-xs text-white/50">{gradeRangeLabel(program.grade_min, program.grade_max)}</p>
      <div className="mt-4 border-t border-white/10 pt-3 text-sm">
        {!program.is_active ? (
          <p className="text-white/70">Registration opens soon. We'll email you when payments are ready.</p>
        ) : eligible.length === 0 ? (
          <p className="text-white/60">Add a player in this grade range to register.</p>
        ) : (
          <ul className="space-y-1">
            {eligible.map((a) => (
              <li key={a.id} className="flex justify-between">
                <span>{a.preferred_name || a.first_name}</span>
                <span className="text-white/60">{enrolledIds.has(a.id) ? "Registered" : "Not registered"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
